import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { ChevronRight, Wallet } from "lucide-react";

import {
    getPaymentsAvailability,
    listPortalPayments,
} from "../../application/portalServices";
import type { PortalPayment } from "../../domain/types";

/** Format a decimal string as euros, never accumulating in floating point. */
const formatMoney = (amount: string): string => {
    if (amount.trim() === "") return "—";
    const value = Number(amount);
    if (!Number.isFinite(value)) return "—";
    return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
    }).format(value);
};

/** Sum in integer cents, so the total matches the figures beside it. */
const totalOf = (payments: PortalPayment[]): string => {
    const cents = payments.reduce((sum, payment) => {
        const value = Number(payment.total);
        return Number.isFinite(value) ? sum + Math.round(value * 100) : sum;
    }, 0);
    return (cents / 100).toFixed(2);
};

/**
 * Tells the patient, on their home page, that something is waiting to be paid.
 *
 * Renders nothing at all when the therapist has not adopted Payments, or when
 * nothing is outstanding — the backend decides, the portal never guesses. A
 * notice that says "you owe nothing" is noise on a page a patient opens to
 * read their notes.
 *
 * Deliberately not alarming. This is somebody's therapist asking to be paid,
 * not a debt collector: it states the amount, links to the detail, and stops.
 */
export default function PendingPaymentsNotice() {
    const { t } = useTranslation();
    const [pending, setPending] = useState<PortalPayment[]>([]);

    useEffect(() => {
        let cancelled = false;

        getPaymentsAvailability()
            .then((data) => {
                if (cancelled || !data.payments_enabled) return;
                return listPortalPayments().then((list) => {
                    if (cancelled) return;
                    setPending(list.filter((payment) => payment.status === "pending"));
                });
            })
            // Silent: a home page that shouts about a failed side request is
            // worse than one that quietly omits a section.
            .catch(() => undefined);

        return () => {
            cancelled = true;
        };
    }, []);

    if (pending.length === 0) return null;

    return (
        <Link
            to="/portal/payments"
            className="mb-8 flex items-center gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 transition-colors hover:bg-amber-100 dark:border-amber-500/30 dark:bg-amber-500/10 dark:hover:bg-amber-500/15"
        >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
                <Wallet className="h-5 w-5" />
            </span>
            <span className="flex-1">
                <span className="block text-sm font-semibold text-amber-900 dark:text-amber-200">
                    {t("portal.dashboard.pendingPayments", { count: pending.length })}
                </span>
                <span className="block text-xs text-amber-800 dark:text-amber-300/80">
                    {t("portal.dashboard.pendingPaymentsTotal", {
                        total: formatMoney(totalOf(pending)),
                    })}
                </span>
            </span>
            <ChevronRight className="h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300" />
        </Link>
    );
}
